/**
 * Reports —— 月報 ＋ 代墊待補回（`ui-spec.md` P-08／P-09 合併一頁）
 * ⚠️ 數字全部由分錄計（API 層），同 Excel 匯出**同一個來源**（唔會兩個數）。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api, currentPeriod, fmtMoney, type Meta, type MonthlyReport } from '../lib/api';

export default function Reports({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [period, setPeriod] = useState(currentPeriod());
  const [r, setR] = useState<MonthlyReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.report(entityId, period).then(setR).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId, period]);

  const table = (title: string, rows: { k: string; v: number; n?: number }[]) => (
    <section className="card">
      <h2>{title}</h2>
      {rows.length === 0 && <p className="note">{t('list.empty')}</p>}
      <table className="ledger">
        <tbody>
          {rows.map((x) => (
            <tr key={x.k}><td>{x.k}</td><td className="num">{fmtMoney(x.v)}</td><td className="num">{x.n ?? ''}</td></tr>
          ))}
        </tbody>
      </table>
    </section>
  );

  return (
    <main className="page">
      <TopBar title={t('report.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.month')}</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
        </div>
        {r && (
          <>
            <div className="totals">
              <div><small>{t('home.income')}</small><b className="pos">{fmtMoney(r.totals.income_cents)}</b></div>
              <div><small>{t('home.expense')}</small><b className="neg">{fmtMoney(r.totals.expense_cents)}</b></div>
              <div><small>{t('report.net')}</small><b className={r.totals.net_cents >= 0 ? 'pos' : 'neg'}>{fmtMoney(r.totals.net_cents)}</b></div>
            </div>
            {r.locked && <p className="err">{t('report.locked')}</p>}
          </>
        )}
        {err && <p className="err">{err}</p>}
      </section>

      {r && table(t('report.by_category_expense'), r.byCategory.map((a) => ({ k: `${a.gl_code} ${a.name_zh}`, v: a.dr_cents - a.cr_cents, n: a.n })))}
      {r && table(t('report.by_category_income'), r.incomeByCategory.map((a) => ({ k: `${a.gl_code} ${a.name_zh}`, v: a.cr_cents - a.dr_cents, n: a.n })))}
      {r && table(t('report.by_paid_by'), r.byPaidBy.map((x) => ({ k: x.label === 'company' ? t('expense.paid_by_company') : t(`expense.paid_by_${x.label}`), v: x.total_cents, n: x.n })))}
      {r && table(t('report.by_method'), r.byPaymentMethod.map((x) => ({ k: t(`method.${x.label}`), v: x.total_cents, n: x.n })))}
      {r && table(t('report.by_project'), r.byProject.map((x) => ({ k: x.label, v: x.total_cents, n: x.n })))}
      {r && !period && table(t('report.by_month'), r.byMonth.map((x) => ({ k: x.label, v: x.total_cents, n: x.n })))}

      <section className="card">
        <h2>{t('report.reimb')}</h2>
        {r?.reimbursements.length === 0 && <p className="note">{t('report.reimb_none')}</p>}
        {r?.reimbursements.map((s) => (
          <div key={s.paid_by}>
            <p>
              <b>{t(`expense.paid_by_${s.paid_by}`)}</b>：
              {t('report.reimb_pending', { amount: fmtMoney(s.pending_cents), n: s.pending_count })}
              ｜{t('report.reimb_settled', { amount: fmtMoney(s.settled_cents) })}
            </p>
            <ul className="list">
              {s.items.map((i) => (
                <li key={i.id}>
                  <span className="no">{i.voucher_no}</span>
                  <Link className="purpose" to={`/vouchers/${i.id}`}>{i.purpose}</Link>
                  <span>{i.txn_date}</span>
                  <span className="neg">{fmtMoney(i.amount_cents)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </main>
  );
}
